import { useEffect, useState } from 'react';
import moment from 'moment';
import { Box } from '@mui/material';

import { FHSkeletonLoader } from '../../base';
import GroupTrainingCalendarCard from './GroupTrainingCalendarCard';
import CalendarCard from './CalendarCard';
import {
  isEventWithinTimeFrame,
  getTimeTopValue,
  getTopValue,
  getUnits,
  pastEventEndsInTimeFrame,
  getOverlapRange,
} from './utils';
import { SupervisionType } from '../../../constants';
import { FORTA_ERROR } from '../../../utils/colors';
import { ServiceCodes } from '../billings/claims/constants';

import type { ICalendarData } from './HistoricalDocumentCalendar';
import type { Moment } from 'moment';

interface ICalendarCardContainerProps {
  isFirstRow?: boolean;
  isLastRow?: boolean;
  loading: boolean;
  data: ICalendarData;
  startTime: Moment;
  endTime: Moment;
}

function CalendarCardContainer({
  isFirstRow,
  isLastRow,
  loading,
  startTime,
  endTime,
  data,
}: ICalendarCardContainerProps) {
  const [timeSliderTop, setTimeSliderTop] = useState<number>(0);
  const showTimeSlider = moment().isSameOrAfter(startTime) && moment().isBefore(endTime);

  const [renderableData, setRenderableData] = useState<any>([]);

  useEffect(() => {
    if (!showTimeSlider) return;
    const timer = setInterval(() => {
      const top = getTimeTopValue(startTime);
      setTimeSliderTop(top);
    }, 60000);
    if (!timeSliderTop) {
      const top = getTimeTopValue(startTime);
      setTimeSliderTop(top);
    }
    return () => clearInterval(timer);
  }, [showTimeSlider, startTime]);

  useEffect(() => {
    const renderable: any = [];
    data.assessment.forEach((assessment) => {
      if (
        isEventWithinTimeFrame(assessment, startTime, endTime) ||
        (pastEventEndsInTimeFrame(assessment, startTime) && isFirstRow)
      ) {
        renderable.push({
          resource: assessment,
          type:
            assessment.service_code === ServiceCodes.AssessmentCode
              ? SupervisionType.Assessment
              : SupervisionType.Assessment2,
          start: moment(assessment.date_start),
          end: moment(assessment.date_start).add(assessment.duration, 'seconds'),
        });
      }
    });

    data.bt.forEach((btSn) => {
      if (
        isEventWithinTimeFrame(btSn, startTime, endTime) ||
        (pastEventEndsInTimeFrame(btSn, startTime) && isFirstRow)
      ) {
        renderable.push({
          resource: btSn,
          type: SupervisionType.Bt,
          start: moment(btSn.date_start),
          end: moment(btSn.date_start).add(btSn.duration, 'seconds'),
        });
      }
    });

    data.client.forEach((clientSn) => {
      if (
        isEventWithinTimeFrame(clientSn, startTime, endTime) ||
        (pastEventEndsInTimeFrame(clientSn, startTime) && isFirstRow)
      ) {
        renderable.push({
          resource: clientSn,
          type: SupervisionType.Client,
          start: moment(clientSn.date_start),
          end: moment(clientSn.date_start).add(clientSn.duration, 'seconds'),
        });
      }
    });

    data.groupParent.forEach((groupSn) => {
      if (
        isEventWithinTimeFrame(groupSn, startTime, endTime) ||
        (pastEventEndsInTimeFrame(groupSn, startTime) && isFirstRow)
      ) {
        renderable.push({
          resource: groupSn,
          type: SupervisionType.GroupParent,
          start: moment(groupSn.date_start),
          end: moment(groupSn.date_start).add(groupSn.duration, 'seconds'),
        });
      }
    });

    data.parent.forEach((parentSn) => {
      if (
        isEventWithinTimeFrame(parentSn, startTime, endTime) ||
        (pastEventEndsInTimeFrame(parentSn, startTime) && isFirstRow)
      ) {
        renderable.push({
          resource: parentSn,
          type: SupervisionType.Parent,
          start: moment(parentSn.date_start),
          end: moment(parentSn.date_start).add(parentSn.expected_duration, 'seconds'),
        });
      }
    });

    data.session.forEach((session) => {
      if (
        isEventWithinTimeFrame(session, startTime, endTime) ||
        (pastEventEndsInTimeFrame(session, startTime) && isFirstRow)
      ) {
        renderable.push({
          resource: session,
          type: 'session',
          start: moment(session.date_start),
          end: moment(session.date_start).add(session.expected_duration, 'seconds'),
        });
      }
    });

    for (let i = 0; i < renderable.length; i++) {
      const currentItem = renderable[i];
      let overlapCount = 1;
      let cardBeforeCurrentCardCount = 0;

      for (let j = 0; j < renderable.length; j++) {
        if (i !== j) {
          const nextItem = renderable[j];
          const { overlap, range } = getOverlapRange(
            { start: currentItem.start, end: currentItem.end },
            { start: nextItem.start, end: nextItem.end },
          );
          if (overlap) {
            if (j < i) {
              cardBeforeCurrentCardCount++;
            }
            if (range?.start) {
              currentItem.start = range.start;
            }
            if (range?.end) {
              currentItem.end = range.end;
            }
            overlapCount++;
          }
        }
      }
      currentItem.width = `${100 / overlapCount}%`;
      currentItem.left = `${(100 / overlapCount) * cardBeforeCurrentCardCount}%`;
    }

    setRenderableData(renderable);
  }, [data, isFirstRow, startTime, endTime]);

  return (
    <Box sx={{ position: 'relative' }}>
      {loading && (
        <Box>
          <FHSkeletonLoader sx={{ marginTop: Math.random() * 5 }} height={30} />
          <FHSkeletonLoader sx={{ marginTop: Math.random() * 2.5 }} height={20} />
        </Box>
      )}
      {showTimeSlider && (
        <Box>
          <Box
            sx={{
              zIndex: 10000,
              position: 'absolute',
              border: `1px solid ${FORTA_ERROR}`,
              width: '100%',
              top: timeSliderTop,
            }}
          ></Box>
          <Box
            sx={{
              position: 'absolute',
              width: 0,
              height: 0,
              zIndex: 10000,
              top: timeSliderTop - 4,
              borderTop: '5px solid transparent',
              borderBottom: '5px solid transparent',
              borderLeft: `7px solid ${FORTA_ERROR}`,
            }}
          ></Box>
        </Box>
      )}
      {renderableData.map(({ resource, type, width, left }: any, i: number) => {
        return type === SupervisionType.GroupParent ? (
          <GroupTrainingCalendarCard
            key={i}
            resource={resource}
            top={getTopValue(startTime, moment(resource.date_start))}
            units={getUnits(
              startTime,
              endTime,
              moment(resource.date_start),
              resource.expected_duration ?? resource.duration,
              Boolean(isFirstRow || isLastRow),
              Boolean(isFirstRow),
            )}
            width={width}
            left={left}
          />
        ) : (
          <CalendarCard
            key={i}
            resource={resource}
            top={getTopValue(startTime, moment(resource.date_start))}
            units={getUnits(
              startTime,
              endTime,
              moment(resource.date_start),
              resource.expected_duration ?? resource.duration,
              Boolean(isFirstRow || isLastRow),
              Boolean(isFirstRow),
            )}
            type={type}
            width={width}
            left={left}
          />
        );
      })}
    </Box>
  );
}

export default CalendarCardContainer;
